<template>
	<v-row class="full-height">
		<v-col cols="12" class="flex-none">
			<div class="status-actions">
				<v-btn @click="$emit('print', 'INVOICE')" v-if="canPrintInvoice()" depressed outlined>
					<v-icon class="mr-1">mdi-printer</v-icon> Print Invoice</v-btn
				>
				<v-btn @click="$emit('print', 'SALES ORDER')" v-if="canPrintOrder()" depressed outlined
					><v-icon class="mr-1">mdi-printer</v-icon> Print Order</v-btn
				>
				<v-btn depressed outlined @click="$emit('cancel')">Back</v-btn>
			</div>
		</v-col>

		<!-- Order Overview -->
		<v-col cols="12" class="flex-none">
			<div class="status-container">
				<div class="status-container-heading">
					<div class="status-container-heading-field">
						<label>Order Number:</label>
						<p>{{ order.orderNumber }}</p>
					</div>
					<div class="status-container-heading-field">
						<label>Order Date:</label>
						<p>{{ formatHeadingDate(order.orderDate) }}</p>
					</div>
					<div class="status-container-heading-field">
						<label>Rep:</label>
						<v-tooltip bottom :disabled="!salesRepName">
							<template v-slot:activator="{ on, attrs }">
								<p v-bind="attrs" v-on="on">{{ salesRepInitials }}</p>
							</template>
							<span>{{ salesRepName }}</span>
						</v-tooltip>
					</div>
				</div>
				<div class="status-information">
					<div class="status-information-details">
						<dl>
							<dt>Bill To:</dt>
							<dd>{{ order.organization }}</dd>
							<dt>&nbsp;</dt>
							<dd>{{ `${billingAddress.addressLine1} ${billingAddress.addressLine2}`.trim() }}</dd>
							<dt>&nbsp;</dt>
							<dd>
								{{ billingAddress.city }}, {{ billingAddress.state }}
								{{ billingAddress.postalCode }}
							</dd>
							<dt>&nbsp;</dt>
							<dd>&nbsp;</dd>
						</dl>

						<dl>
							<dt>Ship To:</dt>
							<dd>{{ order.organization }}</dd>
							<dt>&nbsp;</dt>
							<dd>
								{{ `${shippingAddress.addressLine1} ${shippingAddress.addressLine2}`.trim() }}
							</dd>
							<dt>&nbsp;</dt>
							<dd>
								{{ shippingAddress.city }}, {{ shippingAddress.state }}
								{{ shippingAddress.postalCode }}
							</dd>
							<dt>&nbsp;</dt>
							<dd>&nbsp;</dd>
						</dl>

						<dl>
							<dt>Primary Contact:</dt>
							<dd>{{ `${contact.firstName} ${contact.lastName}` }}</dd>
							<dt>Cell Phone:</dt>
							<dd>{{ contact.cellPhoneNumber }}</dd>
							<dt>Work Phone:</dt>
							<dd>{{ contact.workPhoneNumber }}</dd>
							<dt>Email:</dt>
							<dd>{{ contact.email }}</dd>
						</dl>
					</div>
				</div>
			</div>
		</v-col>

		<!-- Related Order Items Preview -->
		<v-col cols="12" class="flex-none" v-if="ready">
			<div class="status-preview">
				<div class="flex-table" role="table-head">
					<div class="flex-table-row" role="row-group">
						<div class="flex-table-cell" role="column-header">Line</div>
						<div class="flex-table-cell" role="column-header">SKU #</div>
						<div class="flex-table-cell" role="column-header">Description</div>
						<div class="flex-table-cell" role="column-header">Pick Ticket #</div>
						<div class="flex-table-cell" role="column-header">Ordered</div>
						<div class="flex-table-cell" role="column-header">Shipped</div>
						<div class="flex-table-cell" role="column-header">BKO</div>
						<div class="flex-table-cell" role="column-header">Tracking #</div>
						<div class="flex-table-cell" role="column-header">Shipping Date</div>
						<div class="flex-table-cell" role="column-header">Status</div>
					</div>
				</div>
				<div
					v-for="(item, previewIndex) in lineItems"
					:key="previewIndex"
					class="flex-table"
					role="table-body"
				>
					<LineItemStatusEntry :item="item" :level="1"> </LineItemStatusEntry>
				</div>
			</div>
		</v-col>

		<PickTicketStatus
			v-if="canViewWarehouseSections()"
			ref="pickTicketStatus"
			:pick-tickets="pickTickets"
			:status-types="statusTypes"
			:order-id="order.salesOrderId"
			:is-cancelled="order.isCancelled"
			:can-print="canPrintPickTicket()"
			:tracking-urls="trackingUrls"
			:line-items="lineItems"
			@printPickTicket="(id) => $emit('printPickTicket', id)"
			@create-shipping-label="handleCreateShippingLabel"
		/>

		<!-- Fillable Orders -->
		<v-col v-if="canViewWarehouseSections()" cols="12" class="flex-none pb-0">
			<div class="status-table status-table-fill">
				<div class="flex-table" role="table-head">
					<div class="flex-table-row" role="row-group">
						<div
							class="flex-table-cell"
							role="cell"
							:style="fillableOrders.length && fillableOrders.length > 0 ? `` : 'opacity: 0.4;'"
						>
							Fillable Items
							{{
								fillableOrders.length && fillableOrders.length > 0
									? `(${fillableOrders.length})`
									: ""
							}}
							<v-btn
								icon
								@click="
									showTable.includes('fillable-orders')
										? (showTable = showTable.filter((i) => i !== 'fillable-orders'))
										: showTable.push('fillable-orders')
								"
								:disabled="fillableOrders.length === 0 || !fillableOrders"
							>
								<v-icon>mdi-chevron-down</v-icon>
							</v-btn>
						</div>
						<div class="flex-table-cell" role="cell">
							<v-btn
								depressed
								outlined
								:disabled="fillableOrders.length === 0 || !fillableOrders || !canFillOrders() || !hasSelectedFillableItems"
								:loading="isCommitting"
								@click="fillSelectedItems"
							>
								Fill Items
							</v-btn>
						</div>
					</div>
				</div>
				<ExpandTransition>
					<div
						v-show="showTable.includes('fillable-orders') && fillableOrders?.length > 0"
						class="status-table-accordian"
						:class="
							showTable.includes('fillable-orders') && fillableOrders?.length > 0
								? 'status-table-accordian-active'
								: null
						"
					>
						<div class="status-table-fill-items">
							<div class="flex-table" role="table-head">
								<div class="flex-table-row" role="row-group">
									<div class="flex-table-cell" role="cell">&nbsp;</div>
									<div class="flex-table-cell" role="cell">Line #</div>
									<div class="flex-table-cell" role="cell">Style Code</div>
									<div class="flex-table-cell" role="cell">Description</div>
									<div class="flex-table-cell" role="cell">Need</div>
									<div class="flex-table-cell" role="cell">Committed</div>
									<div class="flex-table-cell" role="cell">Available</div>
									<div class="flex-table-cell" role="cell">Status</div>
									<div class="flex-table-cell" role="cell">&nbsp;</div>
								</div>
							</div>
							<div class="flex-table" role="table-body">
								<template v-for="(data, i) in fillableOrders">
									<div :key="i" class="flex-table-row" role="row-group">
										<div class="flex-table-cell" role="cell">
											<v-checkbox
												class="form-checkbox-center"
												v-model="data.selected"
												color="accent"
												hide-details="auto"
												@change="selectAllSizes(data)"
												:disabled="!canFillOrders() || !canFillItem(data)"
											></v-checkbox>
										</div>
										<div class="flex-table-cell" role="cell">
											<span>Line #{{ getLineNumberByLineItemId(data.lineItemId) }}</span>
										</div>
										<div class="flex-table-cell" role="cell">
											<SystemCategoryIcon
												:system-category-id="data.systemCategoryId"
												:inventoryItemId="data.itemId"
											/>
											<div class="form-input">
												<v-text-field
													outlined
													dense
													hide-details="auto"
													v-model="data.styleCode"
													disabled
												></v-text-field>
											</div>
										</div>
										<div class="flex-table-cell" role="cell">
											<div class="form-input">
												<v-text-field
													outlined
													dense
													hide-details="auto"
													v-model="data.description"
													disabled
												></v-text-field>
											</div>
										</div>
										<div class="flex-table-cell" role="cell">
											<div class="form-input">
												<v-text-field
													class="number-input"
													outlined
													dense
													hide-details="auto"
													v-model="data.totalNeed"
													type="number"
													min="0"
													disabled
												></v-text-field>
											</div>
										</div>
										<div class="flex-table-cell" role="cell">
											<div class="form-input">
												<v-text-field
													class="number-input"
													outlined
													dense
													hide-details="auto"
													v-model="data.totalCommitted"
													type="number"
													min="0"
													:disabled="!canFillOrders()"
												></v-text-field>
											</div>
										</div>
										<div class="flex-table-cell" role="cell">
											<div class="form-input">
												<v-text-field
													class="number-input"
													outlined
													dense
													hide-details="auto"
													v-model="data.totalAvailable"
													type="number"
													min="0"
													:disabled="!canFillOrders()"
												></v-text-field>
											</div>
										</div>
										<div class="flex-table-cell" role="cell">
											<div
												class="flex-table-status"
												:class="renderStatus(data.statusId, data).color"
											>
												{{ renderStatus(data.statusId, data).label }}
											</div>
										</div>
										<div class="flex-table-cell" role="cell">
											<v-tooltip bottom v-if="!data.sizes || data.sizes.length === 0">
												<template v-slot:activator="{ on, attrs }">
													<div v-bind="attrs" v-on="on">
														<v-btn icon disabled>
															<v-icon>mdi-chevron-down</v-icon>
														</v-btn>
													</div>
												</template>
												<span>No Sizes</span>
											</v-tooltip>
											<v-btn
												icon
												v-if="data.sizes && data.sizes.length > 0"
												@click="
													showSubTableByRow == `fill-${i}`
														? (showSubTableByRow = null)
														: (showSubTableByRow = `fill-${i}`)
												"
											>
												<v-icon>mdi-chevron-down</v-icon>
											</v-btn>
										</div>

										<ExpandTransition>
											<div
												v-if="showSubTableByRow == `fill-${i}`"
												class="status-table-dropdown"
												:class="
													showSubTableByRow == `fill-${i}` ? 'status-table-dropdown-active' : null
												"
												role="row-group"
											>
												<div class="status-table-fill-sizes">
													<div class="flex-table" role="table-head">
														<div class="flex-table-row" role="row-group">
															<div class="flex-table-cell" role="cell">&nbsp;</div>
															<div class="flex-table-cell" role="cell">Size</div>
															<div class="flex-table-cell" role="cell">Ordered</div>
															<div class="flex-table-cell" role="cell">Committed</div>
															<div class="flex-table-cell" role="cell">
																Available
																<v-btn class="ml-2" icon @click="editAvailable = !editAvailable">
																	<v-icon v-if="!editAvailable">mdi-pencil</v-icon>
																	<v-icon v-else>mdi-check</v-icon>
																</v-btn>
															</div>
														</div>
													</div>
													<div class="flex-table" role="table-body">
														<div
															v-for="(size, i) in data.sizes"
															:key="i"
															class="flex-table-row"
															role="row-group"
														>
															<div class="flex-table-cell" role="cell">
																<v-checkbox
																	class="form-checkbox-center"
																	v-model="size.selected"
																	color="accent"
																	hide-details="auto"
																	:disabled="!canFillOrders() || !canFillSize(size)"
																></v-checkbox>
															</div>
															<div class="flex-table-cell" role="cell">
																<div class="form-input">
																	<v-text-field
																		solo
																		flat
																		dense
																		hide-details="auto"
																		v-model="size.name"
																		readonly
																	></v-text-field>
																</div>
															</div>
															<div class="flex-table-cell" role="cell">
																<div class="form-input">
																	<v-text-field
																		solo
																		flat
																		dense
																		hide-details="auto"
																		v-model="size.customerOrdered"
																		readonly
																	></v-text-field>
																</div>
															</div>
															<div class="flex-table-cell" role="cell">
																<div class="form-input">
																	<v-text-field
																		solo
																		flat
																		dense
																		hide-details="auto"
																		v-model="size.qtyCommitted"
																		readonly
																	></v-text-field>
																</div>
															</div>
															<div class="flex-table-cell" role="cell">
																<div class="form-input">
																	<v-text-field
																		v-if="!editAvailable"
																		solo
																		flat
																		dense
																		hide-details="auto"
																		v-model="size.qtyAvailable"
																		readonly
																	></v-text-field>
																	<v-text-field
																		v-else
																		outlined
																		dense
																		hide-details="auto"
																		v-model="size.qtyAvailable"
																	></v-text-field>
																</div>
															</div>
														</div>
													</div>
												</div>
											</div>
										</ExpandTransition>
									</div>
								</template>
							</div>
						</div>
					</div>
				</ExpandTransition>
			</div>
		</v-col>

		<!-- Backordered Stock Items -->
		<v-col v-if="canViewWarehouseSections()" cols="12" class="flex-none pb-0">
			<div class="status-table status-table-back">
				<div class="flex-table" role="table-head">
					<div class="flex-table-row" role="row-group">
						<div
							class="flex-table-cell"
							role="cell"
							:style="
								backorderedStockItems.length && backorderedStockItems.length > 0
									? ``
									: 'opacity: 0.4;'
							"
						>
							Backordered Stock Items
							{{
								backorderedStockItems.length && backorderedStockItems.length > 0
									? `(${backorderedStockItems.length})`
									: ""
							}}
							<v-btn
								icon
								@click="
									showTable.includes('backordered-stock')
										? (showTable = showTable.filter((i) => i !== 'backordered-stock'))
										: showTable.push('backordered-stock')
								"
								:disabled="backorderedStockItems.length === 0 || !backorderedStockItems"
							>
								<v-icon>mdi-chevron-down</v-icon>
							</v-btn>
						</div>
					</div>
				</div>
				<ExpandTransition>
					<div
						v-show="showTable.includes('backordered-stock') && backorderedStockItems?.length > 0"
						class="status-table-accordian"
						:class="
							showTable.includes('backordered-stock') && backorderedStockItems?.length > 0
								? 'status-table-accordian-active'
								: null
						"
					>
						<div class="status-table-back-items">
							<div class="flex-table" role="table-head">
								<div class="flex-table-row" role="row-group">
									<div class="flex-table-cell" role="cell">&nbsp;</div>
									<div class="flex-table-cell" role="cell">Line #</div>
									<div class="flex-table-cell" role="cell">Style Code</div>
									<div class="flex-table-cell" role="cell">Description</div>
									<div class="flex-table-cell" role="cell">Need</div>
									<div class="flex-table-cell" role="cell">Committed</div>
									<div class="flex-table-cell" role="cell">Available</div>
									<div class="flex-table-cell" role="cell">On Order</div>
									<div class="flex-table-cell" role="cell">Status</div>
									<div class="flex-table-cell" role="cell">&nbsp;</div>
								</div>
							</div>
							<div class="flex-table" role="table-body">
								<template v-for="(data, i) in backorderedStockItems">
									<div :key="i" class="flex-table-row" role="row-group">
										<div class="flex-table-cell" role="cell">
											<v-checkbox
												class="form-checkbox-center"
												v-model="data.selected"
												color="accent"
												hide-details="auto"
												@change="selectAllSizes(data)"
											></v-checkbox>
										</div>
										<div class="flex-table-cell" role="cell">
											<span>Line #{{ getLineNumberByLineItemId(data.lineItemId) }}</span>
										</div>
										<div class="flex-table-cell" role="cell">
											<SystemCategoryIcon
												:system-category-id="data.systemCategoryId"
												:inventoryItemId="data.itemId"
											/>
											<div class="form-input">
												<v-text-field
													outlined
													dense
													hide-details="auto"
													v-model="data.styleCode"
													disabled
												></v-text-field>
											</div>
										</div>
										<div class="flex-table-cell" role="cell">
											<div class="form-input">
												<v-text-field
													outlined
													dense
													hide-details="auto"
													v-model="data.description"
													disabled
												></v-text-field>
											</div>
										</div>
										<div class="flex-table-cell" role="cell">
											<div
												class="form-input"
												:class="{ 'jumplink-wrapper': data.totalNeed > 0 }"
												@click="
													data.totalNeed > 0 &&
														$router.push({
															path: '/purchasing/po-generate',
															query: {
																search: data.orderNumber,
																styleCode: data.styleCode,
																orderNumber: data.orderNumber,
																autoSelect: 'true',
															},
														})
												"
												:title="data.totalNeed > 0 ? 'View in POs to Generate' : ''"
											>
												<v-text-field
													class="number-input"
													outlined
													dense
													hide-details="auto"
													v-model="data.totalNeed"
													type="number"
													min="0"
													disabled
												></v-text-field>
											</div>
										</div>
										<div class="flex-table-cell" role="cell">
											<div class="form-input">
												<v-text-field
													class="number-input"
													outlined
													dense
													hide-details="auto"
													v-model="data.totalCommitted"
													type="number"
													min="0"
													disabled
												></v-text-field>
											</div>
										</div>
										<div class="flex-table-cell" role="cell">
											<div class="form-input">
												<v-text-field
													class="number-input"
													outlined
													dense
													hide-details="auto"
													v-model="data.totalAvailable"
													type="number"
													min="0"
													disabled
												></v-text-field>
											</div>
										</div>
										<div class="flex-table-cell" role="cell">
											<div
												class="form-input"
												:class="{ 'jumplink-wrapper': data.totalOnOrder > 0 }"
												@click="
													data.totalOnOrder > 0 &&
														$router.push({
															path: '/receiving/receiving-entry',
															query: {
																purchaseOrderNumber: data.styleCode,
																filters: 'onlyOpenOrder',
															},
														})
												"
												:title="data.totalOnOrder > 0 ? 'View in Receiving' : ''"
											>
												<v-text-field
													class="number-input"
													outlined
													dense
													hide-details="auto"
													v-model="data.totalOnOrder"
													type="number"
													min="0"
													disabled
												></v-text-field>
											</div>
										</div>
										<div class="flex-table-cell" role="cell">
											<div
												class="flex-table-status"
												:class="renderStatus(data.statusId).color"
												:style="`color:${renderStatus(data.statusId).hex};border-color:${
													renderStatus(data.statusId).hex
												};`"
											>
												{{ renderStatus(data.statusId).label }}
											</div>
										</div>
										<div class="flex-table-cell" role="cell">
											<v-tooltip bottom v-if="!data.sizes || data.sizes.length === 0">
												<template v-slot:activator="{ on, attrs }">
													<div v-bind="attrs" v-on="on">
														<v-btn
															icon
															@click="
																showSubTableByRow == `back-${i}`
																	? (showSubTableByRow = null)
																	: (showSubTableByRow = `back-${i}`)
															"
															:disabled="!data.sizes || data.sizes.length === 0"
														>
															<v-icon>mdi-chevron-down</v-icon>
														</v-btn>
													</div>
												</template>
												<span>No Sizes</span>
											</v-tooltip>
											<v-btn
												icon
												v-if="data.sizes && data.sizes.length > 0"
												@click="
													showSubTableByRow == `back-${i}`
														? (showSubTableByRow = null)
														: (showSubTableByRow = `back-${i}`)
												"
											>
												<v-icon>mdi-chevron-down</v-icon>
											</v-btn>
										</div>

										<ExpandTransition>
											<div
												v-if="showSubTableByRow == `back-${i}`"
												class="status-table-dropdown"
												:class="
													showSubTableByRow == `back-${i}` ? 'status-table-dropdown-active' : null
												"
												role="row-group"
											>
												<div class="status-table-back-sizes">
													<div class="flex-table" role="table-head">
														<div class="flex-table-row" role="row-group">
															<div class="flex-table-cell" role="cell">&nbsp;</div>
															<div class="flex-table-cell" role="cell">Size</div>
															<div class="flex-table-cell" role="cell">Customer Ordered</div>
															<div class="flex-table-cell" role="cell">Need</div>
															<div class="flex-table-cell" role="cell">Committed</div>
															<div class="flex-table-cell" role="cell">
																Available
																<v-btn class="ml-2" icon @click="editAvailable = !editAvailable">
																	<v-icon v-if="!editAvailable">mdi-pencil</v-icon>
																	<v-icon v-else>mdi-check</v-icon>
																</v-btn>
															</div>
															<div class="flex-table-cell" role="cell">
																On Order
																<v-btn class="ml-2" icon>
																	<v-icon>mdi-open-in-new</v-icon>
																</v-btn>
															</div>
														</div>
													</div>
													<div class="flex-table" role="table-body">
														<div
															v-for="(size, i) in data.sizes"
															:key="i"
															class="flex-table-row"
															role="row-group"
														>
															<div class="flex-table-cell" role="cell">
																<v-checkbox
																	class="form-checkbox-center"
																	v-model="size.selected"
																	color="accent"
																	hide-details="auto"
																></v-checkbox>
															</div>
															<div class="flex-table-cell" role="cell">
																<div class="form-input">
																	<v-text-field
																		solo
																		flat
																		dense
																		hide-details="auto"
																		v-model="size.name"
																		readonly
																	></v-text-field>
																</div>
															</div>
															<div class="flex-table-cell" role="cell">
																<div class="form-input">
																	<v-text-field
																		solo
																		flat
																		dense
																		hide-details="auto"
																		v-model="size.customerOrdered"
																		readonly
																	></v-text-field>
																</div>
															</div>
															<div class="flex-table-cell" role="cell">
																<div class="form-input">
																	<v-text-field
																		solo
																		flat
																		dense
																		hide-details="auto"
																		v-model="size.qtyNeed"
																		readonly
																		:class="{ 'need-text': size.qtyNeed > 0 }"
																	></v-text-field>
																</div>
															</div>
															<div class="flex-table-cell" role="cell">
																<div class="form-input">
																	<v-text-field
																		solo
																		flat
																		dense
																		hide-details="auto"
																		v-model="size.qtyCommitted"
																		readonly
																	></v-text-field>
																</div>
															</div>
															<div class="flex-table-cell" role="cell">
																<div class="form-input">
																	<v-text-field
																		v-if="!editAvailable"
																		solo
																		flat
																		dense
																		hide-details="auto"
																		v-model="size.qtyAvailable"
																		readonly
																	></v-text-field>
																	<v-text-field
																		v-else
																		outlined
																		dense
																		hide-details="auto"
																		v-model="size.qtyAvailable"
																	></v-text-field>
																</div>
															</div>
															<div class="flex-table-cell" role="cell">
																<div class="form-input">
																	<v-text-field
																		solo
																		flat
																		dense
																		hide-details="auto"
																		v-model="size.qtyOnOrder"
																		:class="{ 'onOrder-text': size.qtyOnOrder > 0 }"
																		readonly
																	></v-text-field>
																</div>
															</div>
														</div>
													</div>
												</div>
											</div>
										</ExpandTransition>
									</div>
								</template>
							</div>
						</div>
					</div>
				</ExpandTransition>
			</div>
		</v-col>

		<ProcessingPurchaseOrdersStatus
			v-if="canViewWarehouseSections()"
			:sales-order-id="order.salesOrderId"
			:line-items="lineItems"
			:hardRefresh="hardRefreshKey"
			:canPrintVendorPOs="canPrintVendorPOs()"
			@printPO="(ids) => $emit('printPurchaseOrders', ids)"
		/>

		<DropshipOrdersStatus
			v-if="canViewWarehouseSections()"
			:sales-order-id="order.salesOrderId"
			:line-items="lineItems"
			:hardRefresh="hardRefreshKey"
			:canPrintVendorPOs="canPrintVendorPOs()"
			@printPO="(ids) => $emit('printPurchaseOrders', ids)"
			@printPackingSlip="(ids) => $emit('printPackingSlips', ids)"
		/>
		
		<!-- Shipping Label Dialog -->
		<ShippingLabelDialog
			v-model="showShippingDialog"
			:order-id="order.salesOrderId"
			:pick-ticket-id="currentPickTicketId"
			@label-created="handleLabelCreated"
			@close="handleShippingDialogClose"
		/>
		
	</v-row>
</template>

<script>
import LineItemStatusEntry from "@/components/SalesOrders/LineItemStatusEntry";
import ExpandTransition from "@/components/ExpandTransition";
import gql from "graphql-tag";
import { LineItemSizeSummary, LineItemStatusSummary } from "../../models/OrderLineItemSummary";
import PickTicketStatus from "@/components/SalesOrders/PickTicketStatus";
import ProcessingPurchaseOrdersStatus from "@/components/SalesOrders/ProcessingPurchaseOrdersStatus";
import DropshipOrdersStatus from "@/components/SalesOrders/DropshipOrdersStatus";
import SystemCategoryIcon from "@/components/SystemCategoryIcon.vue";
import ShippingLabelDialog from "@/components/Shipping/ShippingLabelDialog.vue";
import { hasPermission, SystemPermissions, shouldEnableHardware } from "@/models/User";
import { getHardwareService } from "@/services/HardwareIntegrationService";

export default {
	name: "OrderStatusPrint",
	components: {
		ExpandTransition,
		PickTicketStatus,
		LineItemStatusEntry,
		ProcessingPurchaseOrdersStatus,
		DropshipOrdersStatus,
		SystemCategoryIcon,
		ShippingLabelDialog,
	},
	props: {
		order: {
			type: Object,
		},
		shippingAddress: {
			type: Object,
		},
		billingAddress: {
			type: Object,
		},
		contact: {
			type: Object,
		},
		hardRefresh: {
			type: Number,
		},
	},
	data() {
		return {
			ready: false,
			lineItems: [],
			showTable: ["pick-tickets"],
			showDateByRow: null,
			showItemTableByRow: null,
			showSubTableByRow: null,
			editAvailable: false,
			showShippingDateByLine: null,
			pickTickets: [],
			fillableOrders: [],
			statusTypes: [
				{
					text: "Not Shipped",
					statusId: "NotShipped",
				},
				{
					text: "Shipped",
					statusId: "Shipped",
				},
			],
			sizeLookupByItem: {},
			posToGenerateLineItems: [],
			hardRefreshKey: 0,
			// Shipping dialog state
			showShippingDialog: false,
			currentPickTicketId: null,
			// Tracking URLs by pick ticket ID
			trackingUrls: {},
			// Fill Items loading state
			isCommitting: false,
		};
	},
	created() {
		this.$bus.$on("print-pickTicket", () => {
			console.log("print-pickTicket initiated");
			setTimeout(async () => {
				await this.init();
				this.$apollo.queries.posToGenerateLineItems.refetch();
			}, 500);
		});
	},
	async mounted() {
		// Only initialize hardware for users who need it (not reps).
		// Reps can view orders but don't need UHB WebSocket, scales, or printers.
		const user = this.$store.getters.userInfo;
		if (user && shouldEnableHardware(user)) {
			await this.initializeHardwareForLabels();
		}
	},
	destroyed() {
		this.$bus.$off("print-pickTicket");
	},
	apollo: {
		pickTickets: {
			query() {
				return gql`
					query GetPickTickets($ids: [Long!]!) {
						pickTicketProcessingTimelines(pickTicketIds: $ids) {
							pickTicketId
							processingDate
							lines {
								lineItemId
								processingDate
								events {
									at
									description
								}
							}
						}
						pickTickets(where: { pickTicketId: { in: $ids } }) {
							items {
								pickTicketId
								shippingDate
								trackingNumber
								printDate: lastPrinted
								type
								normalPickTicketId
								finalShipStationShipmentId
								finalShipStationSalesOrderId
								normalPickTicket {
									pickTicketId
									shippingDate
									trackingNumber
									printDate: lastPrinted
									type
									finalShipStationShipmentId
									finalShipStationSalesOrderId
								}
							}
						}
					}
				`;
			},
			variables() {
				return {
					ids: this.flattenLineItems(this.lineItems)
						.map((i) => i.pickTicketId)
						.filter((id) => id != null),
				};
			},
			update(data) {
				let pts = [];
				let addedIds = new Set();
				// Timestamps are UTC; show them on the viewer's local calendar
				const localDate = (iso) =>
					new Date(iso).toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
				// Each Order Status row's own processing date and the history behind it, computed
				// server-side per row (parents: family's latest arrival; received sub-items: their own
				// receive; never-received sub-items: when the family went in), #1493
				const processingRows = new Map();
				(data.pickTicketProcessingTimelines || []).forEach((t) =>
					t.lines.forEach((row) => processingRows.set(String(row.lineItemId), row))
				);
				const stampProcessing = (row, isProcessing) => {
					const entry = isProcessing ? processingRows.get(String(row.lineItemId)) : null;
					row.processingDate = entry ? localDate(entry.processingDate) : null;
					row.processingHistory = entry
						? entry.events.map((e) => ({ date: localDate(e.at), description: e.description }))
						: [];
				};
				data.pickTickets.items.forEach(async (i) => {
					if (i.shippingDate) {
						i.formattedShippingDate = this.formatDate(i.shippingDate);
						i.shippingDate = this.parseDate(i.formattedShippingDate);
					}

					if (i.printDate) {
						i.formattedPrintDate = this.formatDate(i.printDate);
						i.printDate = this.parseDate(i.formattedPrintDate);
					}

					let items = this.flattenLineItems(this.lineItems).filter(
						(x) => i.pickTicketId == x.pickTicketId
					);
					i.items = items;
					let effectiveType = (i.type === 'PROCESSING' && i.normalPickTicketId) ? 'NORMAL' : i.type;
					const isProcessing = effectiveType === 'PROCESSING';
					items.forEach(x => {
						x.pickTicketType = effectiveType;
						stampProcessing(x, isProcessing);
						if (x.children) x.children.forEach(c => {
							c.pickTicketType = effectiveType;
							stampProcessing(c, isProcessing);
						});
					});
					if (i.type === 'PROCESSING' && !i.normalPickTicketId) {
						i.isShipped = false;
						i.shippingStatus = 'Processing';
					} else {
						i.isShipped = i.shippingDate != null;
						i.shippingStatus = i.isShipped ? "Shipped" : "NotShipped";
					}

					if (!addedIds.has(i.pickTicketId)) {
						pts.push(i);
						addedIds.add(i.pickTicketId);
					}

					if (i.normalPickTicket) {
						let existingNormalPt = pts.find(p => p.pickTicketId === i.normalPickTicket.pickTicketId);
						if (existingNormalPt) {
							// Consolidated Normal PT already added by a previous Processing PT —
							// merge this Processing PT's items into it and re-sort by line number
							items.forEach(item => {
								if (!existingNormalPt.items.some(e => e.lineItemId === item.lineItemId)) {
									existingNormalPt.items.push(item);
								}
							});
							existingNormalPt.items.sort((a, b) => (a.lineNumber || 0) - (b.lineNumber || 0));
						} else {
							let normalPickTicketClone = JSON.parse(JSON.stringify(i.normalPickTicket));

							if (normalPickTicketClone.shippingDate) {
								normalPickTicketClone.formattedShippingDate = this.formatDate(
									normalPickTicketClone.shippingDate
								);
								normalPickTicketClone.shippingDate = this.parseDate(
									normalPickTicketClone.formattedShippingDate
								);
							}

							if (normalPickTicketClone.printDate) {
								normalPickTicketClone.formattedPrintDate = this.formatDate(
									normalPickTicketClone.printDate
								);
								normalPickTicketClone.printDate = this.parseDate(
									normalPickTicketClone.formattedPrintDate
								);
							}

							// Start with this Processing PT's items, plus any items directly on the Normal PT
							let directItems = this.flattenLineItems(this.lineItems).filter(
								(x) => normalPickTicketClone.pickTicketId == x.pickTicketId
							);
							normalPickTicketClone.items = [...items, ...directItems]
								.sort((a, b) => (a.lineNumber || 0) - (b.lineNumber || 0));

							normalPickTicketClone.isShipped = normalPickTicketClone.shippingDate != null;
							normalPickTicketClone.shippingStatus = normalPickTicketClone.isShipped
								? "Shipped"
								: "NotShipped";

							pts.push(normalPickTicketClone);
							addedIds.add(normalPickTicketClone.pickTicketId);
						}
					}
				});
				return pts;
			},
			skip() {
				return !this.ready;
			},
		},
		posToGenerateLineItems: {
			query() {
				return gql`
					query PurchaseOrdersToGenerateGrouped($salesOrderId: Int!) {
						purchaseOrdersToGenerateGrouped(salesOrderId: $salesOrderId) {
							lineItemId
							itemId
							systemCategoryId
							styleCode
							description
							orderNumber
							orderDate
							customer
							companyName
							vendorId
							totalNeed
							totalCommitted
							totalAvailable
							totalOnOrder
							statusId
							sizeIds
							sizeIdsJson
						}
					}
				`;
			},
			update: (data) => {
				return data.purchaseOrdersToGenerateGrouped;
			},
			variables() {
				return {
					salesOrderId: this.order.salesOrderId,
				};
			},
			async result({ data, loading }) {
				if (!loading && data) {
					await this.init();
				}
			},
		},
	},
	methods: {
		// <security>
		canPrintPickTicket() {
			const user = this.$store.getters.userInfo;
			if (hasPermission(user, SystemPermissions.SalesOrders_OrderStatus_PrintPickTicket)) {
				return true;
			}
			return user.isAdmin;
		},
		canFillOrders() {
			const user = this.$store.getters.userInfo;
			if (hasPermission(user, SystemPermissions.SalesOrders_OrderStatus_FillOrders)) {
				return true;
			}
			return user.isAdmin;
		},
		canPrintInvoice() {
			const user = this.$store.getters.userInfo;
			if (hasPermission(user, SystemPermissions.SalesOrders_OrderStatus_PrintInvoice)) {
				return true;
			}
			return user.isAdmin;
		},
		canPrintOrder() {
			const user = this.$store.getters.userInfo;
			if (hasPermission(user, SystemPermissions.SalesOrders_OrderStatus_PrintOrder)) {
				return true;
			}
			return user.isAdmin;
		},
		canPrintVendorPOs() {
			const user = this.$store.getters.userInfo;
			if (hasPermission(user, SystemPermissions.SalesOrders_OrderStatus_PrintVendorPOS)) {
				return true;
			}
			return user.isAdmin;
		},
		canPrintPackingSlips() {
			const user = this.$store.getters.userInfo;
			if (hasPermission(user, SystemPermissions.SalesOrders_OrderStatus_PrintPackingSlips)) {
				return true;
			}
			return user.isAdmin;
		},
		/**
		 * Check if the user can view warehouse-specific sections (PickTicketStatus, Fillable Items,
		 * Backordered Stock Items, Processing Purchase Orders, Dropship Orders).
		 * Allowed for: Warehouse users (FillOrders), Shippers (ViewPickTicket), and Admins.
		 */
		canViewWarehouseSections() {
			const user = this.$store.getters.userInfo;
			if (hasPermission(user, SystemPermissions.SalesOrders_OrderStatus_FillOrders)) {
				return true;
			}
			if (hasPermission(user, SystemPermissions.Shipper_Shipping_ViewPickTicket)) {
				return true;
			}
			return user.isAdmin;
		},
		/**
		 * Check if an item can be filled (has available inventory to commit)
		 * Items that are fully committed (totalAvailable === 0) cannot be filled again
		 */
		canFillItem(item) {
			return item.totalAvailable > 0;
		},
		/**
		 * Check if a size can be filled (has available inventory to commit)
		 * Sizes that are fully committed cannot be filled again
		 */
		canFillSize(size) {
			// Size can be filled if there's available inventory and not fully committed
			return size.qtyAvailable > 0 && size.qtyCommitted < size.customerOrdered;
		},
		// </security>

		async querySizes(lineItems) {
			let res = await this.$apollo.query({
				query: gql`
					query GetItemSizes($ids: [Long!]) {
						sizes: orderSizeInventoryInfo(take: 500, where: { lineItemId: { in: $ids } }) {
							items {
								sizeId
								lineItemId
								inventoryItemId
								name
								showSize
								binNumber
								onHand
								need
								onOrder
								available
								orderQuantityCommitted
								itemQuantityCommitted
							}
						}
					}
				`,
				variables: {
					ids: this.flattenLineItems(lineItems).map((i) => i.lineItemId),
				},
				fetchPolicy: "no-cache",
			});
			let data = res.data;

			this.sizeLookupByItem = this.sizeLookupByItem || {};
			data.sizes.items.forEach((s) => {
				if (!this.sizeLookupByItem[s.inventoryItemId]) {
					this.sizeLookupByItem[s.inventoryItemId] = [];
				}
				this.sizeLookupByItem[s.inventoryItemId].push(s);
			});

			return;
		},
		formatDate(date) {
			if (!date) return "";

			date = date.substring(0, 10);
			const [year, month, day] = date.split("-");
			return `${month}/${day}/${year}`;
		},

		// Dates may arrive as Date objects (assigned by OrderView) or as API strings
		formatHeadingDate(value) {
			if (!value) return "—";
			if (value instanceof Date) {
				const month = String(value.getMonth() + 1).padStart(2, "0");
				const day = String(value.getDate()).padStart(2, "0");
				return `${month}/${day}/${value.getFullYear()}`;
			}
			return this.formatDate(String(value)) || "—";
		},

		renderStatus(statusId, data = null) {
			let status = {
				label: "",
				color: "",
				hex: "",
			};

			if (data && data.totalNeed === 0 && data.totalCommitted > 0) {
				status.label = "Committed";
				status.color = "color-green";
			} else if (data && data.totalCommitted > 0) {
				status.label = "Partial Commit";
				status.color = "color-orange";
			}

			if (status.label === "") {
				switch (statusId) {
					case 1:
						status.label = "Needs to be Ordered";
						status.color = "color-orange";
						break;
					case 2:
						status.label = "On Order";
						status.color = "form-status-gray";
						status.hex = "#424242";
						break;
					case 3:
						status.label = "Fillable";
						status.color = "color-green";
						break;
					default:
						status.label = "Incomplete";
						status.color = "color-orange";
				}
			}

			return status;
		},

		isSubItem(sysCategoryId) {
			return this.$systemCategories.isSubItem(sysCategoryId);
		},

		selectAllSizes(data) {
			data.sizes = data.sizes.map((size) => {
				if (data.selected) {
					return {
						...size,
						selected: this.canFillSize(size),
					};
				} else {
					return {
						...size,
						selected: false,
					};
				}
			});
		},

		async getLineItems() {
			const res = await this.$http.get(`/api/orders/${this.order.salesOrderId}/lineItems`);
			return res.data.lineItems;
		},

		flattenLineItems(lineItems) {
			let itemsArr = [];
			lineItems.forEach((li) => itemsArr.push(...this.flattenLineItem(li)));

			return itemsArr;
		},

		flattenLineItem(lineItem) {
			let lineItems = [lineItem];

			if (lineItem.children?.length > 0) {
				lineItem.children.forEach((child) => {
					lineItems.push(...this.flattenLineItem(child));
				});
			}

			return lineItems;
		},

		/**
		 * @returns {LineItemStatusSummary }
		 */
		toLineItemSummary(lineItem) {
			return new LineItemStatusSummary(
				lineItem.lineItemId,
				lineItem.itemId,
				lineItem.lineNumber,
				lineItem.styleCode,
				lineItem.description,
				lineItem.stockedItem,
				lineItem.quantity,
				lineItem.status,
				lineItem.pickTicketId,
				lineItem.normalPickTicketId,
				lineItem.systemCategoryId,
				lineItem.shippingDate,
				this.calculateBySizeInfo(lineItem),
				lineItem.children?.map((c) => this.toLineItemSummary(c)),
				lineItem.backOrdered,
				lineItem.dropShip,
				lineItem.dropShipShipped,
				lineItem.trackingNumbers,
				lineItem.returnTrackingNumbers
			);
		},
		/**
		 * @returns {LineItemSizeSummary[]}
		 */
		calculateBySizeInfo(lineItem) {
			let sizeInfo = {};
			this.sizeLookupByItem[lineItem.itemId]?.forEach((s) => {
				return (sizeInfo[s.sizeId] = new LineItemSizeSummary(
					s.sizeId,
					s.name,
					0,
					s.committed,
					s.onHand,
					0
				));
			});

			lineItem.sizeSelections?.forEach((selection) => {
				if (sizeInfo[selection.sizeId]) {
					sizeInfo[selection.sizeId].ordered += selection.quantity;
				}
			});

			return sizeInfo;
		},

		mapSizes() {
			if (!this.posToGenerateLineItems) return [];

			// Recursive function to find a line item by lineItemId
			const findLineItem = (lineItems, lineItemId) => {
				for (let item of lineItems) {
					if (item.lineItemId === lineItemId) {
						return item;
					}
					if (item.children && item.children.length > 0) {
						let found = findLineItem(item.children, lineItemId);
						if (found) return found;
					}
				}
				return null;
			};

			this.posToGenerateLineItems = this.posToGenerateLineItems.map((item) => {
				let sizesForItem = this.sizeLookupByItem ? this.sizeLookupByItem[item.itemId] : [];
				let correspondingLineItem = findLineItem(this.lineItems, item.lineItemId);
				let sizes = sizesForItem
					? sizesForItem
							.filter(
								(size) =>
									size.lineItemId === item.lineItemId &&
									item.sizeIds.includes(size.sizeId)
							)
							.map((size) => ({
								sizeId: size.sizeId,
								name: size.name,
								qtyNeed: size.need,
								qtyCommitted: size.orderQuantityCommitted,
								qtyAvailable: size.available,
								qtyOnOrder: size.onOrder,
								customerOrdered:
									correspondingLineItem && correspondingLineItem.sizes[size.sizeId]
										? correspondingLineItem.sizes[size.sizeId].ordered
										: 0,
							}))
							.sort((a, b) => a.sizeId - b.sizeId)
					: [];

				return {
					...item,
					sizes: sizes,
				};
			});
		},

		getLineNumberByLineItemId(lineItemId) {
			// Recursive function to search through items and their children
			const searchItems = (items, id) => {
				for (let item of items) {
					if (item.lineItemId === id) {
						return item.lineNumber;
					}

					if (item.children && item.children.length > 0) {
						const childResult = searchItems(item.children, id);
						if (childResult !== null) {
							return childResult;
						}
					}
				}
				return null;
			};

			// Start the search from the root
			const result = searchItems(this.lineItems, lineItemId);

			// Return the result or default to 1
			return result !== null ? result : 1;
		},

		parseDate(date) {
			//use with vuetify calander inputs
			if (!date) return null;
			const [month, day, year] = date.split("/");
			return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
		},

		async init() {
			let lineItems = await this.getLineItems();
			this.flattenLineItems(lineItems).forEach((li, index) => (li.lineNumber = index + 1));

			this.sizeLookupByItem = [];
			await this.querySizes(lineItems);
			this.lineItems = lineItems.map((li) => this.toLineItemSummary(li));

			this.mapSizes();
			this.$apollo.queries.pickTickets.refetch();
			this.ready = true;
			this.hardRefreshKey++;
		},
		
		// Shipping label methods
		handleCreateShippingLabel(pickTicketId) {
			this.currentPickTicketId = pickTicketId;
			this.showShippingDialog = true;
		},
		
		async handleLabelCreated(data) {
			console.log('=== LABEL CREATED EVENT RECEIVED ===');
			console.log('Full event data:', JSON.stringify(data, null, 2));
			console.log('pickTicketId:', data.pickTicketId);
			console.log('trackingNumber:', data.trackingNumber);
			console.log('labelData:', data.labelData);
			
			// Extract tracking number from different possible locations
			let trackingNumber = data.trackingNumber;
			if (!trackingNumber && data.labelData) {
				// Check if trackingNumber is in labelData.label (camelCase)
				if (data.labelData.label && data.labelData.label.trackingNumber) {
					trackingNumber = data.labelData.label.trackingNumber;
				}
				// Check if tracking_number is in labelData.label (snake_case)
				else if (data.labelData.label && data.labelData.label.tracking_number) {
					trackingNumber = data.labelData.label.tracking_number;
				}
				// Check if trackingNumber is directly in labelData
				else if (data.labelData.trackingNumber) {
					trackingNumber = data.labelData.trackingNumber;
				}
				// Check if tracking_number is directly in labelData
				else if (data.labelData.tracking_number) {
					trackingNumber = data.labelData.tracking_number;
				}
			}
			
			console.log('Extracted tracking number:', trackingNumber);
			
			// Extract carrier code to determine tracking URL
			let carrierCode = null;
			if (data.labelData && data.labelData.shipment) {
				carrierCode = data.labelData.shipment.carrierCode || data.labelData.shipment.carrier_code;
			}
			
			// Build tracking URL based on carrier
			let trackingUrl = null;
			if (trackingNumber && carrierCode) {
				const carrierUrls = {
					'ups': `https://www.ups.com/track?loc=en_US&tracknum=${trackingNumber}`,
					'fedex': `https://www.fedex.com/apps/fedextrack/?action=track&trackingnumber=${trackingNumber}`,
					'usps': `https://tools.usps.com/go/TrackConfirmAction?qtc_tLabels1=${trackingNumber}`,
					'stamps_com': `https://tools.usps.com/go/TrackConfirmAction?qtc_tLabels1=${trackingNumber}`
				};
				trackingUrl = carrierUrls[carrierCode.toLowerCase()] || carrierUrls['usps'];
				console.log('Built tracking URL:', trackingUrl);
				
				// Store the tracking URL for this pick ticket
				if (data.pickTicketId) {
					this.$set(this.trackingUrls, data.pickTicketId, trackingUrl);
				}
			}
			
			// Update pick ticket with tracking number
			if (data.pickTicketId && trackingNumber) {
				// Find the pick ticket and update its tracking number
				const pickTicket = this.pickTickets.find(pt => 
					pt.pickTicketId === data.pickTicketId
				);
				
				console.log('Found pick ticket:', pickTicket);
				console.log('All pick tickets:', this.pickTickets);
				
				if (pickTicket) {
					// Update local data immediately for better UX
					pickTicket.trackingNumber = trackingNumber;
					if (!pickTicket.shippingDate) {
						pickTicket.shippingDate = new Date().toISOString();
					}
					
					console.log('Updated pick ticket locally:', pickTicket);
					
					// Call the update API to save tracking number
					try {
						console.log('Updating pick ticket via API...');
						const res = await this.$http.post(`/api/pick-tickets/${data.pickTicketId}`, {
							trackingNumber: trackingNumber,
							shippingDate: pickTicket.shippingDate || new Date().toISOString()
						});
						
						console.log('Update API response:', res);
						
						if (res.status === 200) {
							console.log('Pick ticket updated successfully');
							
							// Always print the pick ticket after label creation to mark it as shipped
							console.log('Auto-printing pick ticket after label creation...');
							console.log('Print URL:', `/api/orders/${this.order.salesOrderId}/printPickTicket/${data.pickTicketId}`);
							
							const printRes = await this.$http.post(`/api/orders/${this.order.salesOrderId}/printPickTicket/${data.pickTicketId}`);
							console.log('Print API response:', printRes);
							
							if (printRes.status === 200) {
								console.log('Pick ticket printed and marked as shipped');
								this.$bus.$emit("print-pickTicket");
								this.$emit('printPickTicket', data.pickTicketId);
							} else {
								console.error('Print API returned non-200 status:', printRes.status);
							}
							
							// Refresh pick tickets data and wait for it to complete
							console.log('Refreshing pick tickets data...');
							await this.$apollo.queries.pickTickets.refetch();
							
							// Force component update
							this.$forceUpdate();
						} else {
							console.error('Update API returned non-200 status:', res.status);
						}
					} catch (error) {
						console.error('Error updating pick ticket:', error);
						console.error('Error details:', error.response);
						// Revert local change on error
						pickTicket.trackingNumber = null;
					}
				} else {
					console.error('Pick ticket not found in local data:', data.pickTicketId);
					console.error('Available pick ticket IDs:', this.pickTickets.map(pt => pt.pickTicketId));
				}
			} else {
				console.error('Missing required data - pickTicketId:', data.pickTicketId, 'trackingNumber:', trackingNumber);
			}
		},
		
		handleShippingDialogClose() {
			this.showShippingDialog = false;
			this.currentPickTicketId = null;
		},
		
		/**
		 * Initialize hardware services for label printing
		 * This ensures printers are available even when navigating directly to order status
		 */
		async initializeHardwareForLabels() {
			try {
				console.log('[OrderStatusPrint] Initializing hardware services for label printing...');

				// Get the singleton hardware service instance
				const hardwareService = getHardwareService();

				// Check if hardware service is available and initialize if needed
				const availability = await hardwareService.checkAvailability();
				console.log('[OrderStatusPrint] Hardware availability:', availability);

				if (availability.isAvailable) {
					// Initialize the service (this will connect to UHB and discover devices)
					await hardwareService.initialize();
					console.log('[OrderStatusPrint] Hardware service initialized successfully');

					// Trigger printer discovery to populate the printer cache
					// This is important for the ShippingLabelForm to have printers available
					const printers = await hardwareService.discoverPrinters();
					console.log('[OrderStatusPrint] Discovered printers:', printers.length);

					if (printers.length > 0) {
						console.log('[OrderStatusPrint] Available printers:', printers.map(p => p.name));
					} else {
						console.log('[OrderStatusPrint] No printers discovered');
					}
				} else {
					console.log('[OrderStatusPrint] Hardware service not available:', availability.error);
				}
			} catch (error) {
				// Hardware initialization is non-critical - don't block the UI
				console.warn('[OrderStatusPrint] Hardware initialization failed (non-critical):', error);
			}
		},

		/**
		 * Fill selected fillable items by committing inventory
		 */
		async fillSelectedItems() {
			if (!this.canFillOrders()) return;

			this.isCommitting = true;
			try {
				// Build request payload from selected items
				const ordersToCommit = this.buildCommitPayload();

				if (ordersToCommit.length === 0) {
					this.$bus.$emit("showAlert", {
						message: "No items selected for filling",
						type: "warning",
					});
					return;
				}

				const response = await this.$http.post("/api/receiving/commit", {
					orders: ordersToCommit,
					isDecommit: false,
				});

				if (response.status === 200) {
					this.$bus.$emit("showAlert", {
						message: "Items filled successfully",
						type: "success",
					});

					// Clear selections
					this.fillableOrders.forEach((order) => {
						order.selected = false;
						if (order.sizes) {
							order.sizes.forEach((size) => (size.selected = false));
						}
					});

					// Refresh data
					await this.$apollo.queries.posToGenerateLineItems.refetch();
					await this.init();
				}
			} catch (error) {
				console.error("Error filling items:", error);
				const errorMessage =
					error.response?.data?.message ||
					error.response?.data ||
					"Failed to fill items";
				this.$bus.$emit("showAlert", {
					message: errorMessage,
					type: "error",
				});
			} finally {
				this.isCommitting = false;
			}
		},

		/**
		 * Build the commit payload from selected fillable items
		 * @returns {Array} Array of FillableOrdersDetail objects for the commit API
		 */
		buildCommitPayload() {
			const payload = [];

			for (const order of this.fillableOrders) {
				// Skip items that can't be filled (already fully committed)
				if (!this.canFillItem(order)) continue;

				// If parent is selected, include all sizes that can be filled
				if (order.selected) {
					if (order.sizes && order.sizes.length > 0) {
						for (const size of order.sizes) {
							// Only include sizes that can still be filled
							if (this.canFillSize(size)) {
								payload.push(this.buildCommitItem(order, size));
							}
						}
					} else if (!order.sizeIds || order.sizeIds.length === 0) {
						// Truly non-sized item
						payload.push(this.buildCommitItem(order, null));
					}
				} else if (order.sizes) {
					// Check individual size selections
					for (const size of order.sizes) {
						if (size.selected && this.canFillSize(size)) {
							payload.push(this.buildCommitItem(order, size));
						}
					}
				}
			}

			return payload;
		},

		/**
		 * Build a single commit item matching the FillableOrdersDetail schema
		 * @param {Object} order - The fillable order item
		 * @param {Object|null} size - The size object, or null for non-sized items
		 * @returns {Object} FillableOrdersDetail object
		 */
		buildCommitItem(order, size) {
			return {
				salesOrderId: this.order.salesOrderId,
				orderNumber: this.order.orderNumber,
				orderDate: this.order.orderDate || new Date().toISOString(),
				lineItemId: order.lineItemId,
				itemId: order.itemId,
				stock: true, // Fillable items are stock items
				systemCategoryId: order.systemCategoryId,
				styleCode: order.styleCode,
				description: order.description,
				sizeId: size?.sizeId || null,
				size: size?.name || null,
				organization: this.order.organization,
				ordered: size ? size.customerOrdered : order.totalNeed,
				orderQuantityCommitted: size ? size.qtyCommitted : order.totalCommitted,
				available: size ? size.qtyAvailable : order.totalAvailable,
				status: "Fillable",
				pickTicketId: null,
			};
		},
	},
	computed: {
		salesRep() {
			if (!this.order || !this.order.salesRepId || !Array.isArray(this.order.salesReps)) {
				return null;
			}
			return this.order.salesReps.find((r) => r.salesRepId == this.order.salesRepId) || null;
		},
		salesRepName() {
			return this.salesRep ? this.salesRep.name : "";
		},
		// Initials derivation matches PickTicketPrintTemplateOptimized.extractInitials (issue #1123)
		salesRepInitials() {
			const name = this.salesRepName;
			if (!name) return "—";
			if (name.length <= 3) return name.toUpperCase();
			const parts = name.trim().split(/\s+/);
			if (parts.length >= 2) {
				return (parts[0][0] + parts[1][0]).toUpperCase();
			}
			return name.substring(0, 2).toUpperCase();
		},
		backorderedStockItems() {
			return this.posToGenerateLineItems.filter((order) => order.statusId !== 3);
		},
		hasSelectedFillableItems() {
			return this.fillableOrders.some(
				(order) =>
					// Item is selected AND can be filled
					(order.selected && this.canFillItem(order)) ||
					// Or has sizes selected that can be filled
					order.sizes?.some((s) => s.selected && this.canFillSize(s))
			);
		},
	},
	watch: {
		posToGenerateLineItems(newVal, oldVal) {
			// Include items that are fillable (statusId === 3) AND either:
			// - Have available inventory to commit (totalAvailable > 0), OR
			// - Have already been committed (totalCommitted > 0)
			// Items only drop off when a pick ticket is generated (handled by backend PickTicketId IS NULL filter)
			this.fillableOrders = this.posToGenerateLineItems
				? this.posToGenerateLineItems.filter(
						(order) => order.statusId === 3 && (order.totalAvailable > 0 || order.totalCommitted > 0)
				  )
				: [];
		},
		async hardRefresh() {
			this.$apollo.queries.posToGenerateLineItems.refetch();
		},
	},
};
</script>

<style lang="scss" scoped>
.jumplink-wrapper {
	cursor: pointer;

	.v-input {
		pointer-events: none;
	}

	::v-deep .v-input__slot {
		border-color: $color-primary !important;
		background-color: rgba($color-primary, 0.05) !important;

		input {
			color: $color-primary !important;
		}
	}

	&:hover {
		::v-deep .v-input__slot {
			background-color: rgba($color-primary, 0.12) !important;
		}
	}
}

.status {
	&-actions {
		display: flex;
		width: 100%;
		justify-content: flex-end;

		.v-btn {
			background-color: $color-white;
			border-color: $color-gray;
			text-transform: initial;

			&:not(:last-child) {
				margin-right: 0.8rem;
			}
		}
	}

	&-container {
		position: relative;
		display: block;
		background-color: $color-white;
		border: 1px solid $color-gray;
		border-radius: 3px;
		padding: 2.5rem;

		&-heading {
			display: flex;
			flex-wrap: wrap;
			column-gap: 4rem;
			row-gap: 0.5rem;
			border-bottom: 1px solid $color-gray;
			padding-bottom: 1.5rem;
			margin-bottom: 1.5rem;

			&-field {
				display: flex;
			}

			label {
				font-size: 1.6rem;
				color: $color-gray;
				padding-right: 0.5rem;
			}

			p {
				font-size: 1.6rem;
				color: $color-black;
				margin-bottom: 0;
			}
		}
	}

	&-information {
		&-details {
			display: flex;
			flex-flow: row nowrap;
			justify-content: space-evenly;
			dl {
				display: flex;
				flex-flow: row wrap;

				dt {
					flex-basis: 25%;
				}

				dd {
					flex-basis: 70%;
					flex-grow: 1;
					padding-left: 1rem;
				}
			}
		}
	}

	&-preview {
		position: relative;
		border-radius: 3px;
		overflow: hidden;

		&-title {
			font-size: 1.4rem;
		}

		.flex-table[role="table-body"] {
			background-color: $color-white;
			border-top: none;
		}

		.flex-table-cell {
			padding-left: 0.5rem;
			padding-right: 0.5rem;

			&:nth-child(1) {
				width: 3%;
				justify-content: center;
			}
			&:nth-child(2) {
				width: 15%;
			}
			&:nth-child(3) {
				width: 29%;
			}
			&:nth-child(4) {
				width: 7%;
			}
			&:nth-child(5) {
				width: 5%;
				justify-content: center;
			}
			&:nth-child(6) {
				width: 5%;
				justify-content: center;
			}
			&:nth-child(7) {
				width: 5%;
				justify-content: center;
			}
			&:nth-child(8) {
				width: 13%;
			}
			&:nth-child(9) {
				width: 10%;
				justify-content: center;
			}
			&:nth-child(10) {
				width: 8%;
				justify-content: center;
				align-items: center;
				text-align: center;
			}
		}

		.form-input {
			position: relative;
			margin: 0;
			width: 100%;
			flex-wrap: wrap;

			& > .v-input {
				width: 100%;
				margin-top: 0.8rem;
			}
		}

		&-line {
			&-number {
				margin: auto;
			}
		}

		&-level {
			display: inline-flex;
			justify-content: center;
			align-items: center;
			border: 1px solid transparent;
			border-radius: 5px;
			min-height: 5rem;
			min-width: 2rem;
			margin-right: 1rem;
		}
	}

	&-table {
		border-radius: 3px;
		overflow: hidden;

		&-fill {
			position: relative;
			width: 100%;

			& > .flex-table {
				//table-head
				&[role="table-head"] {
					border-bottom: 1px solid $color-gray;

					.flex-table-row {
						background-color: $color-white;
						min-height: 6rem;
					}
					.flex-table-cell {
						padding: 0rem 0.8rem;

						&:nth-child(1) {
							width: 70%;
						}
						&:nth-child(2) {
							width: 30%;
							justify-content: flex-end;

							.v-btn {
								margin-left: 0.8rem;
							}
						}
					}
				}
			}

			&-items {
				position: relative;
				background-color: $color-gray;
				padding: 3rem 2rem;

				& > .flex-table {
					&[role="table-head"] {
						border: 1px solid $color-gray-dark;
						border-bottom: none;
						border-radius: 5px 5px 0 0;
						overflow: hidden;

						.flex-table-row {
							background-color: $color-white;
						}
					}

					&[role="table-body"] {
						border: 1px solid $color-gray-dark;
						background-color: $color-white;
						border-radius: 0 0 5px 5px;
						overflow: hidden;
					}

					.flex-table-row {
						.flex-table-cell {
							&:nth-child(1) {
								width: 4%;
								justify-content: center;
								align-items: center;
							}
							&:nth-child(2) {
								width: 6%;
							}
							&:nth-child(3) {
								width: 20%;
							}
							&:nth-child(4) {
								width: 34%;
							}
							&:nth-child(5) {
								width: 7%;
							}
							&:nth-child(6) {
								width: 8%;
							}
							&:nth-child(7) {
								width: 7%;
							}
							&:nth-child(8) {
								width: 10%;
							}
							&:nth-child(9) {
								width: 4%;
								justify-content: center;
								align-items: center;
							}
						}
					}

					.form-input {
						width: 100%;
						margin: initial;

						label {
							font-size: 1.3rem;
							width: initial;
						}
					}
				}

				.color-orange {
					border-color: $color-orange;
					color: $color-orange;
				}

				.color-green {
					border-color: $color-green;
					color: $color-green;
				}
			}

			&-sizes {
				position: relative;
				background-color: $color-gray;
				padding: 3rem 25%;

				& > .flex-table {
					&[role="table-head"] {
						border: 1px solid $color-gray-dark;
						border-bottom: none;
						border-radius: 5px 5px 0 0;
						overflow: hidden;

						.flex-table-row {
							background-color: $color-gray-dark;
							min-height: 6rem;
							color: $color-white;
						}
					}

					&[role="table-body"] {
						border: 1px solid $color-gray-dark;
						background-color: $color-white;
						border-radius: 0 0 5px 5px;
						overflow: hidden;

						.flex-table-row {
							border-top: 1px solid $color-gray-dark;
						}
					}

					.flex-table-row {
						.flex-table-cell {
							&:nth-child(1) {
								justify-content: center;
								width: 7%;
							}
							&:nth-child(2) {
								justify-content: center;
								width: 18%;
							}
							&:nth-child(3) {
								justify-content: center;
								width: 25%;
							}
							&:nth-child(4) {
								justify-content: center;
								width: 25%;
							}
							&:nth-child(5) {
								justify-content: center;
								width: 25%;

								position: relative;
								padding-left: 3.5rem;
								padding-right: 3.5rem;

								.v-btn {
									position: absolute;
									right: 0;

									.v-icon {
										font-size: 1.8rem;
										color: $color-white;
									}
								}
							}

							.v-text-field {
								::v-deep input {
									text-align: center;
								}
							}
						}
					}
				}
			}
		}

		&-back {
			position: relative;
			width: 100%;

			& > .flex-table {
				//table-head
				&[role="table-head"] {
					border-bottom: 1px solid $color-gray;

					.flex-table-row {
						background-color: $color-white;
						min-height: 6rem;
					}
					.flex-table-cell {
						padding: 0rem 0.8rem;

						&:nth-child(1) {
							width: 70%;
						}
						&:nth-child(2) {
							width: 30%;
							justify-content: flex-end;

							.v-btn {
								margin-left: 0.8rem;
							}
						}
					}
				}
			}

			&-items {
				position: relative;
				background-color: $color-gray;
				padding: 3rem 2rem;

				& > .flex-table {
					&[role="table-head"] {
						border: 1px solid $color-gray-dark;
						border-bottom: none;
						border-radius: 5px 5px 0 0;
						overflow: hidden;

						.flex-table-row {
							background-color: $color-white;
						}
					}

					&[role="table-body"] {
						border: 1px solid $color-gray-dark;
						background-color: $color-white;
						border-radius: 0 0 5px 5px;
						overflow: hidden;
					}

					.flex-table-row {
						.flex-table-cell {
							&:nth-child(1) {
								width: 4%;
								justify-content: center;
								align-items: center;
							}
							&:nth-child(2) {
								width: 6%;
							}
							&:nth-child(3) {
								width: 19%;
							}
							&:nth-child(4) {
								width: 29%;
							}
							&:nth-child(5) {
								width: 7%;
							}
							&:nth-child(6) {
								width: 8%;
							}
							&:nth-child(7) {
								width: 7%;
							}
							&:nth-child(8) {
								width: 7%;
							}
							&:nth-child(9) {
								width: 9%;
							}
							&:nth-child(10) {
								width: 4%;
								justify-content: center;
								align-items: center;
							}
						}
					}

					.form-input {
						width: 100%;
						margin: initial;

						label {
							font-size: 1.3rem;
							width: initial;
						}
					}
				}

				.color-orange {
					border-color: $color-orange;
					color: $color-orange;
				}

				.color-green {
					border-color: $color-green;
					color: $color-green;
				}
			}

			&-sizes {
				position: relative;
				background-color: $color-gray;
				padding: 3rem 25%;

				& > .flex-table {
					&[role="table-head"] {
						border: 1px solid $color-gray-dark;
						border-bottom: none;
						border-radius: 5px 5px 0 0;
						overflow: hidden;

						.flex-table-row {
							background-color: $color-gray-dark;
							min-height: 6rem;
							color: $color-white;
						}
					}

					&[role="table-body"] {
						border: 1px solid $color-gray-dark;
						background-color: $color-white;
						border-radius: 0 0 5px 5px;
						overflow: hidden;

						.flex-table-row {
							border-top: 1px solid $color-gray-dark;
						}
					}

					.flex-table-row {
						.flex-table-cell {
							&:nth-child(1) {
								justify-content: center;
								width: 7%;
							}
							&:nth-child(2) {
								justify-content: center;
								width: 11%;
							}
							&:nth-child(3) {
								justify-content: center;
								width: 18%;
							}
							&:nth-child(4) {
								justify-content: center;
								width: 14%;
							}
							&:nth-child(5) {
								justify-content: center;
								width: 14%;
							}
							&:nth-child(6) {
								justify-content: center;
								width: 18%;

								position: relative;
								padding-left: 3.5rem;
								padding-right: 3.5rem;

								.v-btn {
									position: absolute;
									right: -0.5rem;

									.v-icon {
										font-size: 1.8rem;
										color: $color-white;
									}
								}
							}
							&:nth-child(7) {
								justify-content: center;
								width: 18%;

								position: relative;
								padding-left: 3.5rem;
								padding-right: 3.5rem;

								.v-btn {
									position: absolute;
									right: 0;

									.v-icon {
										font-size: 1.8rem;
										color: $color-white;
									}
								}
							}

							.v-text-field {
								::v-deep input {
									text-align: center;
								}
							}
						}
					}
				}
			}
		}

		&-process {
			position: relative;
			width: 100%;

			& > .flex-table {
				//table-head
				&[role="table-head"] {
					border-bottom: 1px solid $color-gray;

					.flex-table-row {
						background-color: $color-white;
						min-height: 6rem;
					}
					.flex-table-cell {
						padding: 0rem 0.8rem;

						&:nth-child(1) {
							width: 70%;
						}
						&:nth-child(2) {
							width: 30%;
							justify-content: flex-end;
						}
					}
				}

				//table-body
				&[role="table-body"] {
					.flex-table-row {
						background-color: $color-white;

						&:first-of-type {
							border-top: none;
						}
					}
					.flex-table-cell {
						&:nth-child(1) {
							width: 20%;
						}
						&:nth-child(2) {
							width: 16%;
						}
						&:nth-child(3) {
							width: 18%;
						}
						&:nth-child(4) {
							width: 23%;
						}
						&:nth-child(5) {
							width: 15%;
						}
						&:nth-child(6) {
							width: 4%;
							justify-content: center;
							align-items: flex-end;
						}
						&:nth-child(7) {
							width: 4%;
							justify-content: center;
							align-items: flex-end;
						}
					}
				}

				.form-input {
					width: 100%;
					margin: initial;

					label {
						font-size: 1.3rem;
						width: initial;
					}
				}
			}

			.color-orange {
				border-color: $color-orange;
				color: $color-orange;
			}

			.color-green {
				border-color: $color-green;
				color: $color-green;
			}

			&-items {
				position: relative;
				background-color: $color-gray;
				padding: 3rem 2rem;

				& > .flex-table {
					&[role="table-head"] {
						border: 1px solid $color-gray-dark;
						border-bottom: none;
						border-radius: 5px 5px 0 0;
						overflow: hidden;
					}

					&[role="table-body"] {
						border: 1px solid $color-gray-dark;
						background-color: $color-white;
						border-radius: 0 0 5px 5px;
						overflow: hidden;
					}

					.flex-table-row {
						.flex-table-cell {
							&:nth-child(1) {
								width: 6%;
							}
							&:nth-child(2) {
								width: 20%;
							}
							&:nth-child(3) {
								width: 33%;
							}
							&:nth-child(4) {
								width: 8%;
							}
							&:nth-child(5) {
								width: 8%;
							}
							&:nth-child(6) {
								width: 9%;
								align-items: center;
							}
							&:nth-child(7) {
								width: 12%;
								align-items: center;
							}
							&:nth-child(8) {
								width: 4%;
								justify-content: center;
								align-items: center;
							}
						}
					}
				}
			}

			&-sizes {
				position: relative;
				background-color: $color-gray;
				padding: 3rem 25%;

				& > .flex-table {
					&[role="table-head"] {
						border: 1px solid $color-gray-dark;
						border-bottom: none;
						border-radius: 5px 5px 0 0;
						overflow: hidden;

						.flex-table-row {
							background-color: $color-gray-dark;
							min-height: 6rem;
							color: $color-white;
						}
					}

					&[role="table-body"] {
						border: 1px solid $color-gray-dark;
						background-color: $color-white;
						border-radius: 0 0 5px 5px;
						overflow: hidden;

						.flex-table-row {
							border-top: 1px solid $color-gray-dark;
						}
					}

					.flex-table-row {
						.flex-table-cell {
							&:nth-child(1) {
								justify-content: center;
								width: 25%;
							}
							&:nth-child(2) {
								justify-content: center;
								width: 25%;
							}
							&:nth-child(3) {
								justify-content: center;
								width: 25%;
							}
							&:nth-child(4) {
								justify-content: center;
								width: 25%;
							}

							.v-text-field {
								::v-deep input {
									text-align: center;
								}
							}
						}
					}
				}
			}
		}

		&-dropship {
			position: relative;
			width: 100%;

			& > .flex-table {
				//table-head
				&[role="table-head"] {
					border-bottom: 1px solid $color-gray;

					.flex-table-row {
						background-color: $color-white;
						min-height: 6rem;
					}
					.flex-table-cell {
						padding: 0rem 0.8rem;

						&:nth-child(1) {
							width: 70%;
						}
						&:nth-child(2) {
							width: 30%;
							justify-content: flex-end;

							.v-btn {
								margin-left: 0.8rem;
							}
						}
					}
				}
			}
		}

		// Expand/collapse is animated by ExpandTransition (JS-measured max-height);
		// CSS only defines the settled states. Never reintroduce a fixed max-height
		// cap here: a capped flex column squeezes its rows when content exceeds the
		// cap, making rows visually overlap on large orders.
		&-dropdown {
			flex-grow: 1;
			position: relative;
			max-height: 0;
			min-height: 0;
			border: none;
			overflow: hidden;

			&-active {
				max-height: fit-content;
			}
		}

		&-accordian {
			position: relative;
			flex-grow: 1;
		}
	}
}
</style>

<style lang="scss">
.need-text,
.onOrder-text {
	font-weight: bold;
}
.to-order {
	margin-left: 8px;
	cursor: pointer;

	&:hover {
		color: #1264f6;
	}
}
</style>
